import * as React from 'react'

import { useLazyQuery } from '@apollo/client/react'

import {
  CanvaIcon,
  DropboxIcon,
  GoogleDriveIcon,
  GooglePhotosIcon,
  OneDriveIcon,
  UnsplashIcon,
  toast,
} from '@buffer-mono/popcorn'
import { UploadSource } from '@buffer-mono/uploader'
import type { Uploader } from '@buffer-mono/uploader'

import {
  UploadcareIntegrations,
  UploadcareSources,
} from '~publish/components/UploadcareImageDialog/constants'
import type { Service } from '~publish/legacy/constants/services/graphqlTypes'
import { useOpenCanva } from '~publish/legacy/canva/useOpenCanvaDesign'
import { useCanvaDesignConfig } from '~publish/legacy/canva/util/config'
import {
  getCanvaRatioMenuItems,
  getCanvaRatioMenuItemTitle,
  getCanvaRatioMenuItemDescription,
} from '~publish/legacy/canva/util/ratioOptions'
import { useIsIntegrationUploading } from '~publish/legacy/integrations-bar/state/useIsIntegrationUploading'
import { logError } from '~publish/legacy/utils/logError'

import {
  CANVA_SERVICE,
  DROPBOX_SERVICE,
  GOOGLE_DRIVE_SERVICE,
  GOOGLE_PHOTOS_SERVICE,
  ONE_DRIVE_SERVICE,
  UNSPLASH_SERVICE,
} from './constants'
import { TRACK_IMAGES_QUERY } from './queries'
import {
  BUTTON_CLICKED,
  MEDIA_ATTACHED,
  sendIntegrationsEvent,
} from './tracking'
import type {
  GraphImage,
  IntegrationsContext,
  IntegrationsService,
} from './types'
import { openUploadcareModal } from '../uploadcare/openUploadcareModal'

/*
 * HOOK: useIntegrationServices
 */
export const useIntegrationServices = ({
  context,
  uploader,
  updateType,
  serviceName = 'omni',
  onError,
  enableCanva = true,
  enableUnsplash = true,
}: {
  context: IntegrationsContext
  uploader: Pick<Uploader, 'addFilesFromUrls' | 'getId'>
  updateType?: string
  serviceName?: Service | 'omni' | 'instagramStory'
  onError?: ({ message }: { message: string }) => void
  enableCanva?: boolean
  enableUnsplash?: boolean
}): {
  services: Array<IntegrationsService>
  activeService: IntegrationsService | null
} => {
  const [activeService, setActiveService] =
    React.useState<IntegrationsService | null>(null)

  const serviceClose = (): void => {
    setActiveService(null)
  }

  const serviceOpen = (service: IntegrationsService): void => {
    service.onClose = serviceClose
    setActiveService(service)
    sendIntegrationsEvent(BUTTON_CLICKED, context, service.id)

    service.onClick?.()
  }

  React.useEffect(() => {
    if (activeService) {
      // Pushing the change to the end of the call stack
      const timer = setTimeout(() => {
        document.body.style.pointerEvents = ''
      }, 0)

      return (): void => clearTimeout(timer)
    } else {
      document.body.style.pointerEvents = 'auto'
    }
  }, [activeService])

  const { onUploadStarted, onUploadFinished } = useIsIntegrationUploading()

  const [trackImages] = useLazyQuery(TRACK_IMAGES_QUERY)

  if (!onError) {
    onError = ({ message }: { message: string }): void => {
      toast.warning(message, { duration: Infinity })
    }
  }

  /*
   * uploadMediaFile
   *
   * Fetches the file from url, calls uploader.addFilesFromUrls,
   * sends MEDIA_ATTACHED event for integrations and set activeService=null
   */
  const uploadMediaFile = async (
    url: string,
    source: UploadSource,
    name?: string,
    downloadUrl?: string,
  ): Promise<void> => {
    const remoteFile = {
      remoteUrl: url,
      name,
    }
    try {
      onUploadStarted()
      serviceClose()

      const files = await uploader.addFilesFromUrls(remoteFile, { source })

      sendIntegrationsEvent(
        MEDIA_ATTACHED,
        context,
        source.name,
        files.successful[0]?.type,
      )

      if (source.name === UNSPLASH_SERVICE) {
        trackImages({
          variables: {
            trackUrl: downloadUrl,
          },
        })
      }
    } catch (error) {
      logError(error, { metaData: { integration: source.name, remoteFile } })
      onError?.({
        message: `Failed to upload media ${
          error instanceof Error ? error.message : String(error)
        }`,
      })
    } finally {
      // The composer disables posting while this flag is set, so it has to be
      // cleared even when the upload throws. Otherwise the draft stays locked
      // until the page is reloaded.
      onUploadFinished()
    }
  }

  const openUploadCare = (integration: string, sourceName: string): void => {
    openUploadcareModal(
      serviceName === 'instagramStory' ? 'instagram' : serviceName,
      uploader,
      onError,
      integration,
      () =>
        onUploadStarted({ source: sourceName, uploaderId: uploader.getId() }),
      onUploadFinished,
      UploadcareSources.BAR,
      undefined,
      updateType,
      context,
    )
  }

  const canvaService =
    serviceName === 'youtube' || serviceName === 'whatsapp'
      ? 'omni'
      : serviceName
  const isStoryPost = serviceName === 'instagramStory'
  const openCanva = useOpenCanva({
    uploader,
    service: canvaService,
    isStoryPost,
    onClose: serviceClose,
  })
  const canvaDesignConfig = useCanvaDesignConfig(canvaService, isStoryPost)
  const canvaRatioItems = React.useMemo(
    () => getCanvaRatioMenuItems(canvaDesignConfig.dimensions),
    [canvaDesignConfig],
  )

  // INCLUDES HERE THE SERVICES FOR THE INTEGRATIONS BAR
  const services: IntegrationsService[] = [
    {
      id: CANVA_SERVICE,
      title: 'Canva',
      label: 'Design with Canva',
      placeholder: '', // not required for canva
      isNew: false,
      isEnabled: enableCanva,
      icon: <CanvaIcon />,
      openSearchImagesModal: false,
      onClick: openCanva,
      subItems: canvaRatioItems.map((item) => ({
        id: item.id,
        label: getCanvaRatioMenuItemTitle(item),
        description: getCanvaRatioMenuItemDescription(item),
        onClick: (): void =>
          openCanva({
            dimensions: {
              width: item.dimensions.width,
              height: item.dimensions.height,
            },
            ratio: item.ratio,
          }),
      })),
    },
    {
      id: DROPBOX_SERVICE,
      title: 'Dropbox',
      label: 'Select from Dropbox',
      isNew: false,
      isEnabled: true,
      icon: <DropboxIcon />,
      openSearchImagesModal: false,
      onClick: (): void => {
        openUploadCare(UploadcareIntegrations.DROPBOX, 'Dropbox')
      },
    },
    {
      id: GOOGLE_DRIVE_SERVICE,
      title: 'Google Drive',
      label: 'Select from Google Drive',
      isNew: false,
      isEnabled: true,
      icon: <GoogleDriveIcon />,
      openSearchImagesModal: false,
      onClick: (): void => {
        openUploadCare(UploadcareIntegrations.GOOGLE_DRIVE, 'Google Drive')
      },
    },
    {
      id: GOOGLE_PHOTOS_SERVICE,
      title: 'Google Photos',
      label: 'Select from Google Photos',
      isNew: false,
      isEnabled: true,
      icon: <GooglePhotosIcon />,
      openSearchImagesModal: false,
      onClick: (): void => {
        openUploadCare(UploadcareIntegrations.GOOGLE_PHOTOS, 'Google Photos')
      },
    },

    {
      id: ONE_DRIVE_SERVICE,
      title: 'OneDrive',
      label: 'Select from OneDrive',
      isNew: false,
      isEnabled: true,
      icon: <OneDriveIcon />,
      openSearchImagesModal: false,
      onClick: (): void => {
        openUploadCare(UploadcareIntegrations.ONE_DRIVE, 'OneDrive')
      },
    },
    {
      id: UNSPLASH_SERVICE,
      title: 'Unsplash',
      label: 'Select from Unsplash',
      placeholder: 'Search free high resolution photos',
      isNew: false,
      isEnabled: enableUnsplash,
      icon: <UnsplashIcon />,
      openSearchImagesModal: true,
      onFileSelected: async (imageTarget: GraphImage): Promise<void> => {
        uploadMediaFile(
          imageTarget.regularUrl,
          UploadSource.unsplash({
            id: imageTarget.id,
            author: imageTarget.author,
          }),
          `${imageTarget.id}-unsplash.jpg`,
          imageTarget.downloadUrl,
        )
      },
    },
  ]

  return {
    services: services
      .filter((s) => s.isEnabled)
      .map((s) => {
        return Object.assign({}, s, {
          onClick: () => serviceOpen(s),
          subItems: s.subItems?.map((item) => ({
            ...item,
            // Spread to avoid mutating `s`; override onClick so serviceOpen
            // runs the sub-item's own action with the shared tracking/active-service handling.
            onClick: (): void => serviceOpen({ ...s, onClick: item.onClick }),
          })),
        })
      }),
    activeService,
  }
}
